import { useCallback } from 'react';
import useAnalyticsClient from '../client/use-analytics-client';

// Component-facing facade. Each method maps to a single typed client.track call;
// the package fans out to PostHog and teacher-totter.
export default function useTracking() {
  const client = useAnalyticsClient();

  const safeTrack = useCallback((eventName, props) => {
    if (!client) return;

    try {
      const result = client.track(eventName, props || {});

      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }
    } catch (error) {
      // Tracking is best-effort and must never interrupt user actions.
    }
  }, [client]);

  const trackAssignmentConfirmed = useCallback((props) => {
    safeTrack('assignment.confirmation', props);
  }, [safeTrack]);
  const trackAssignmentHistoryView = useCallback((props) => {
    safeTrack('assignment.history-view', props);
  }, [safeTrack]);
  const trackClassFilterChanged = useCallback((props) => {
    safeTrack('filter.class-changed', props);
  }, [safeTrack]);
  const trackGroupFilterChanged = useCallback((props) => {
    safeTrack('filter.group-changed', props);
  }, [safeTrack]);
  const trackCourseFilterChanged = useCallback((props) => {
    safeTrack('filter.course-changed', props);
  }, [safeTrack]);
  const trackFilterApplied = useCallback((props) => {
    safeTrack('filter.applied', props);
  }, [safeTrack]);
  const trackGridCheckboxClick = useCallback((props) => {
    safeTrack('grid.checkbox-click', props);
  }, [safeTrack]);
  const trackSelectAll = useCallback((props) => {
    safeTrack('grid.select-all', props);
  }, [safeTrack]);
  const trackActivityPreview = useCallback((props) => {
    safeTrack('activity.preview', props);
  }, [safeTrack]);
  const trackViewToggle = useCallback((props) => {
    safeTrack('ui.view-toggle', props);
  }, [safeTrack]);

  return {
    trackAssignmentConfirmed,
    trackAssignmentHistoryView,
    trackClassFilterChanged,
    trackGroupFilterChanged,
    trackCourseFilterChanged,
    trackFilterApplied,
    trackGridCheckboxClick,
    trackSelectAll,
    trackActivityPreview,
    trackViewToggle
  };
}
